import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { createDocmanTrigger, updateDocmanTrigger } from '../../../../store/participantService';
import { getObjFromId } from '../../../../utils/directusFunctions';
import {
    COMMUNICATION_TEMPLATES_ERROR,
    PARTICIPANT_STATUS_DETAILS_ERROR,
    PARTICIPANT_STATUS_REASONS_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { trimEntries } from '../../../../utils/objectUtils';
import MultiSelect from '../../../formElements/MultiSelect';
import RadioButtons from '../../../formElements/RadioButtons';
import SingleSelect from '../../../formElements/SingleSelect';
import TextAreaField from '../../../formElements/TextAreaField';
import TextInputField from '../../../formElements/TextInputField';
import FormActions from '../../../ui/formActions/FormActions';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import {
    DESCRIPTION_LIMIT,
    SNOMED_CODE_LIMIT,
    SNOMED_ID_LIMIT,
    validate
} from './validateCreateDocmanTrigger';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const _CREATED_MESSAGE = 'Docman trigger created';
const _UPDATED_MESSAGE = 'Docman trigger updated';

const CreateDocmanTrigger = ({ contractId, trigger = {}, formType, onClose }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        active: true,
        description: '',
        emailTemplateId: '',
        gdm: false,
        snomedCode: '',
        snomedId: '',
        statusId: '',
        statusReasonIds: []
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialEntryState, setInitialEntryState] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const participantStatusDetails = useSelector(
        (state) => state.entities.directusService.participantStatusDetails
    );
    const participantStatusReasonDetails = useSelector(
        (state) => state.entities.directusService.participantStatusReasonDetails
    );
    const communicationTemplates = useSelector(
        (state) => state.entities.communicationService.communicationTemplates
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        const updatedState = {
            ...initialState,
            ...trigger,
            contractId
        };
        setNewEntry(updatedState);
        setInitialEntryState(updatedState);
    }, [contractId, trigger?.id]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _CREATED_MESSAGE || successMessage === _UPDATED_MESSAGE) onClose();
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (!participantStatusDetails?.length) return PARTICIPANT_STATUS_DETAILS_ERROR;
        if (!participantStatusReasonDetails?.length) return PARTICIPANT_STATUS_REASONS_ERROR;
        if (!communicationTemplates?.length) return COMMUNICATION_TEMPLATES_ERROR;
    };

    const clearError = (key) => {
        errors[key] && setErrors(trimEntries([key], errors));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, initialEntryState);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        formType === 'create'
            ? dispatch(createDocmanTrigger(newEntry))
            : dispatch(updateDocmanTrigger(newEntry));
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={`${form.formWrapper} ${app.fadein}`}>
            <form className={form.form} onSubmit={onSubmit} data-testid="edit_docman_trigger_form">
                <h2 className={app.sectionHeading}>
                    {formType === 'create' ? 'Create Trigger' : 'Edit Trigger'}
                </h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextInputField
                            id="snomedCode"
                            label="SNOMED Code"
                            placeholder="Enter SNOMED code..."
                            mandatory={true}
                            maxLength={SNOMED_CODE_LIMIT}
                            value={newEntry.snomedCode}
                            error={errors.snomedCode}
                            onChange={(e) => onUpdate('snomedCode', e.target.value)}
                        />
                        <TextInputField
                            id="snomedId"
                            label="SNOMED ID"
                            placeholder="Enter SNOMED ID..."
                            mandatory={true}
                            maxLength={SNOMED_ID_LIMIT}
                            value={newEntry.snomedId}
                            error={errors.snomedId}
                            onChange={(e) => onUpdate('snomedId', e.target.value)}
                        />
                        <SingleSelect
                            id="emailTemplateId"
                            label="Email Template"
                            placeholder="Select email template..."
                            mandatory={true}
                            menuItems={communicationTemplates}
                            selectedId={newEntry.emailTemplateId || ''}
                            selected={getObjFromId(
                                communicationTemplates,
                                newEntry.emailTemplateId
                            )}
                            error={errors.emailTemplateId}
                            onChange={(chosenId) => onUpdate('emailTemplateId', chosenId)}
                        />
                        <RadioButtons
                            id="gdm"
                            label="Previous GDM Diagnosis"
                            value={newEntry.gdm}
                            onChange={(option) => onUpdate('gdm', option)}
                        />
                        <TextAreaField
                            id="description"
                            label="Description"
                            placeholder={'Enter description...'}
                            mandatory={true}
                            maxLength={DESCRIPTION_LIMIT}
                            value={newEntry.description || ''}
                            count={`${newEntry.description.length}/${DESCRIPTION_LIMIT}`}
                            error={errors.description}
                            onChange={(e) => onUpdate('description', e.target.value)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="statusId"
                            label="Status"
                            placeholder="Select a status..."
                            menuItems={participantStatusDetails}
                            selectedId={newEntry.statusId || ''}
                            selected={getObjFromId(participantStatusDetails, newEntry.statusId)}
                            error={errors.statusId}
                            onChange={(chosenId) => onUpdate('statusId', chosenId)}
                        />
                        <MultiSelect
                            id="statusReasonIds"
                            label="Status Reasons"
                            placeholder="Select status reasons..."
                            menuItems={participantStatusReasonDetails || []}
                            preSelectedIds={newEntry.statusReasonIds || []}
                            preSelects={newEntry.statusReasonIds.map((el) =>
                                getObjFromId(participantStatusReasonDetails, el)
                            )}
                            error={errors.statusReasonIds}
                            onChange={(chosenIds) => onUpdate('statusReasonIds', chosenIds)}
                        />
                        <RadioButtons
                            id="active"
                            label="Active"
                            value={newEntry.active}
                            onChange={(option) => onUpdate('active', option)}
                        />
                    </div>
                </div>

                <FormActions
                    onClose={onSubmit}
                    onCancel={onClose}
                    disabled={isButtonDisabled}
                    btnText={formType === 'create' ? 'ADD NEW TRIGGER' : 'UPDATE TRIGGER'}
                    customClass="noTopBorder"
                    error={errors?.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

CreateDocmanTrigger.propTypes = {
    onClose: PropTypes.func,
    formType: PropTypes.string,
    trigger: PropTypes.object,
    contractId: PropTypes.string
};

export default CreateDocmanTrigger;
